import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  LinearProgress,
  Paper,
  IconButton,
  Card,
  CardContent,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { uploadFile } from '../../services/media';

interface FileUploaderProps {
  open: boolean;
  onClose: () => void;
  currentFolderId?: number | null;
  onUploadComplete?: () => void;
}

interface FileUploadState {
  file: File;
  id?: number;
  name: string;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

const FileUploader: React.FC<FileUploaderProps> = ({
  open,
  onClose,
  currentFolderId,
  onUploadComplete,
}) => {
  const [files, setFiles] = useState<FileUploadState[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) return;
    
    const fileList = Array.from(event.target.files);
    const newFiles = fileList.map(file => ({
      file,
      name: file.name,
      progress: 0,
      status: 'pending' as const,
    }));
    
    setFiles(prevFiles => [...prevFiles, ...newFiles]);
    
    // Clear the input value so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleRemoveFile = (index: number) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };
  
  const handleUpload = async () => {
    if (files.length === 0) return;
    
    setUploading(true);
    setUploadComplete(false);
    
    // Upload files one by one
    for (let i = 0; i < files.length; i++) {
      const fileState = files[i];
      
      if (fileState.status === 'success') continue;
      
      // Update status to uploading
      setFiles(prevFiles => 
        prevFiles.map((f, idx) => 
          idx === i ? { ...f, status: 'uploading', progress: 0 } : f
        )
      );
      
      try {
        // Simulate progress
        const progressInterval = setInterval(() => {
          setFiles(prevFiles => 
            prevFiles.map((f, idx) => 
              idx === i && f.progress < 90 ? { ...f, progress: f.progress + 10 } : f
            )
          );
        }, 200);
        
        // Perform actual upload
        const response = await uploadFile(fileState.file, currentFolderId);
        
        clearInterval(progressInterval);
        
        if (response.success && response.file) {
          setFiles(prevFiles => 
            prevFiles.map((f, idx) => 
              idx === i ? { 
                ...f, 
                status: 'success', 
                progress: 100, 
                id: response.file!.id 
              } : f
            )
          );
        } else {
          setFiles(prevFiles => 
            prevFiles.map((f, idx) => 
              idx === i ? { 
                ...f, 
                status: 'error', 
                progress: 0, 
                error: response.message || 'Upload failed' 
              } : f
            )
          );
        }
      } catch (error) {
        setFiles(prevFiles => 
          prevFiles.map((f, idx) => 
            idx === i ? { 
              ...f, 
              status: 'error', 
              progress: 0,
              error: 'Upload failed due to a network error'
            } : f
          )
        );
      }
    }
    
    setUploading(false);
    setUploadComplete(true);
    
    if (onUploadComplete) {
      onUploadComplete();
    }
  };
  
  const handleCloseDialog = () => {
    if (!uploading) {
      onClose();
      setFiles([]);
      setUploadComplete(false);
    }
  };
  
  const dropzoneRef = useRef<HTMLDivElement>(null);
  const [dragActive, setDragActive] = useState<boolean>(false);
  
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      const newFiles = droppedFiles.map(file => ({
        file,
        name: file.name,
        progress: 0,
        status: 'pending' as const,
      }));
      
      setFiles(prevFiles => [...prevFiles, ...newFiles]);
    }
  };
  
  const renderUploadStatus = () => {
    const totalFiles = files.length;
    const successFiles = files.filter(f => f.status === 'success').length;
    const errorFiles = files.filter(f => f.status === 'error').length;
    const pendingFiles = totalFiles - successFiles - errorFiles;
    
    return (
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography variant="body2">
          {successFiles} of {totalFiles} files uploaded successfully
          {errorFiles > 0 && `, ${errorFiles} failed`}
          {pendingFiles > 0 && `, ${pendingFiles} pending`}
        </Typography>
      </Box>
    );
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={handleCloseDialog}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Upload Files
        <IconButton
          aria-label="close"
          onClick={handleCloseDialog}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          disabled={uploading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Dropzone area */}
        <Box
          ref={dropzoneRef}
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
          sx={{
            border: '2px dashed',
            borderColor: dragActive ? 'primary.main' : 'divider',
            borderRadius: 1,
            p: 3,
            textAlign: 'center',
            bgcolor: dragActive ? 'action.hover' : 'background.default',
            mb: 3,
            transition: 'all 0.2s'
          }}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            multiple
            hidden
            accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/zip"
          />
          <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Drag & Drop files here
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            or
          </Typography>
          <Button
            variant="outlined"
            component="label"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            Browse Files
          </Button>
          <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 1 }}>
            Supported file types: Images, PDF, Word, Text, ZIP
          </Typography>
        </Box>
        
        {/* File list */}
        {files.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Files to upload ({files.length})
            </Typography>
            <Grid container spacing={2}>
              {files.map((file, index) => (
                <Grid item xs={12} key={index}>
                  <Paper 
                    variant="outlined"
                    sx={{ 
                      p: 2, 
                      display: 'flex', 
                      alignItems: 'center',
                      bgcolor: 
                        file.status === 'success' ? 'success.light' : 
                        file.status === 'error' ? 'error.light' : 
                        'background.paper',
                      opacity: file.status === 'success' ? 0.8 : 1
                    }}
                  >
                    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                      <Typography variant="body1" noWrap title={file.name}>
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {(file.file.size / 1024).toFixed(1)} KB
                      </Typography>
                      
                      {file.status === 'uploading' && (
                        <LinearProgress 
                          variant="determinate" 
                          value={file.progress} 
                          sx={{ mt: 1 }} 
                        />
                      )}
                      
                      {file.status === 'error' && (
                        <Typography variant="caption" color="error" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                          <ErrorIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {file.error || 'Upload failed'}
                        </Typography>
                      )}
                      
                      {file.status === 'success' && (
                        <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                          <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />
                          Upload complete
                        </Typography>
                      )}
                    </Box>
                    
                    {file.status !== 'uploading' && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveFile(index)}
                        disabled={uploading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
            
            {uploadComplete && renderUploadStatus()}
          </Box>
        )}
        
        {files.length === 0 && (
          <Card variant="outlined" sx={{ bgcolor: 'background.default', mb: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="textSecondary">
                No files selected for upload
              </Typography>
            </CardContent>
          </Card>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog} disabled={uploading}>
          {uploadComplete ? 'Close' : 'Cancel'}
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleUpload}
          disabled={files.length === 0 || uploading}
          startIcon={uploading ? <LinearProgress value={20} /> : <UploadIcon />}
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileUploader;
